'use client';

import * as React from 'react';

import type { BiDataOverrides } from '@haaretz/s-data-structure-types';

export type BiPageviewOverrides = {
  biData: BiDataOverrides;
  /**
   * Distinguishes virtual pageviews sent from the same URL, e.g. personal-area
   * sections or cancellation stages. CommonUtils combines it with the pathname
   * to build the dedup key of the pageview request.
   */
  pageviewKey?: string;
} | null;

const BiPageviewOverridesContext = React.createContext<BiPageviewOverrides | undefined>(undefined);
const BiPageviewOverridesSetter = React.createContext<React.Dispatch<
  React.SetStateAction<BiPageviewOverrides>
> | null>(null);

/**
 * Lets a page contribute extra fields (e.g. `stage`) to the pageview BI
 * request that CommonUtils sends. Wrap the page content, including the
 * common slot, with this provider, then report the overrides with
 * `useSetBiPageviewOverrides` from any component inside it.
 */
export function BiPageviewOverridesProvider({ children }: React.PropsWithChildren) {
  const [overrides, setOverrides] = React.useState<BiPageviewOverrides>(null);

  return (
    <BiPageviewOverridesContext value={overrides}>
      <BiPageviewOverridesSetter value={setOverrides}>{children}</BiPageviewOverridesSetter>
    </BiPageviewOverridesContext>
  );
}

// `undefined` means there is no provider; `null` means a provider is waiting
// for a child to report the pageview data.
export function useBiPageviewOverrides(): BiPageviewOverrides | undefined {
  return React.useContext(BiPageviewOverridesContext);
}

export function useSetBiPageviewOverrides(): React.Dispatch<
  React.SetStateAction<BiPageviewOverrides>
> {
  const setter = React.useContext(BiPageviewOverridesSetter);

  if (!setter) {
    throw new Error(
      'useSetBiPageviewOverrides was called outside of a BiPageviewOverridesProvider. The reported overrides will be ignored.'
    );
  }

  return setter;
}
